import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
// NOTE: This creates a circular ESM import with editor.store.ts.
// That is safe because neither module accesses the other's exports at
// module-evaluation time — only inside action function bodies, which run
// after both modules are fully initialized (ESM live-binding guarantee).
import { useEditorStore } from './editor.store'
import type { Page, ProjectContent, ProjectTheme } from './editor.store'

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_HISTORY = 50

// ─── Types ────────────────────────────────────────────────────────────────────

type EditorSnapshot = {
  pages: Page[]
  projectContent: ProjectContent
  projectTheme: ProjectTheme
}

type HistoryState = {
  past: EditorSnapshot[]   // snapshots saved before each undoable mutation
  future: EditorSnapshot[] // snapshots available for redo
  activeBatchKey: string | null
}

type HistoryActions = {
  /**
   * Call BEFORE mutating pages. Saves current pages into `past` and clears
   * `future`. Called by editor.store's undoable actions.
   */
  pushSnapshot: (pages: Page[]) => void
  beginBatch: (key: string) => void
  endBatch: (key?: string) => void
  undo: () => void
  redo: () => void
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useHistoryStore = create<HistoryState & HistoryActions>()(
  devtools(
    (set, get) => ({
      past: [],
      future: [],
      activeBatchKey: null,

      pushSnapshot: (pages) =>
        set((state) => {
          if (state.activeBatchKey) return state
          const editor = useEditorStore.getState()
          const snapshot: EditorSnapshot = {
            pages,
            projectContent: editor.projectContent,
            projectTheme: editor.projectTheme,
          }
          return {
            past: [...state.past.slice(-(MAX_HISTORY - 1)), snapshot],
            future: [],
          }
        }, false, 'pushSnapshot'),

      beginBatch: (key) =>
        set((state) => {
          if (state.activeBatchKey === key) return state
          const editor = useEditorStore.getState()
          const snapshot: EditorSnapshot = {
            pages: editor.pages,
            projectContent: editor.projectContent,
            projectTheme: editor.projectTheme,
          }
          return {
            activeBatchKey: key,
            past: [...state.past.slice(-(MAX_HISTORY - 1)), snapshot],
            future: [],
          }
        }, false, 'beginBatch'),

      endBatch: (key) =>
        set((state) => {
          if (key && state.activeBatchKey && state.activeBatchKey !== key) return state
          if (!state.activeBatchKey) return state
          return { activeBatchKey: null }
        }, false, 'endBatch'),

      undo: () => {
        const { past, future } = get()
        if (past.length === 0) return

        const editor = useEditorStore.getState()
        const current: EditorSnapshot = {
          pages: editor.pages,
          projectContent: editor.projectContent,
          projectTheme: editor.projectTheme,
        }
        const previous = past[past.length - 1]!

        set(
          { past: past.slice(0, -1), future: [current, ...future], activeBatchKey: null },
          false,
          'undo'
        )
        const activePageStillExists = previous.pages.some((p) => p.id === editor.activePage)
        useEditorStore.setState({
          pages: previous.pages,
          projectContent: previous.projectContent,
          projectTheme: previous.projectTheme,
          ...(activePageStillExists ? {} : { activePage: previous.pages.find((p) => p.isHome)?.id ?? previous.pages[0]?.id ?? null }),
          activeElement: null,
          selectedElements: [],
          isEditingText: false,
          editOpenId: null,
        })
      },

      redo: () => {
        const { past, future } = get()
        if (future.length === 0) return

        const editor = useEditorStore.getState()
        const current: EditorSnapshot = {
          pages: editor.pages,
          projectContent: editor.projectContent,
          projectTheme: editor.projectTheme,
        }
        const next = future[0]!

        set(
          { past: [...past, current], future: future.slice(1), activeBatchKey: null },
          false,
          'redo'
        )
        const activePageStillExists = next.pages.some((p) => p.id === editor.activePage)
        useEditorStore.setState({
          pages: next.pages,
          projectContent: next.projectContent,
          projectTheme: next.projectTheme,
          ...(activePageStillExists ? {} : { activePage: next.pages.find((p) => p.isHome)?.id ?? next.pages[0]?.id ?? null }),
          activeElement: null,
          selectedElements: [],
          isEditingText: false,
          editOpenId: null,
        })
      },
    }),
    { name: 'HistoryStore', enabled: import.meta.env.DEV }
  )
)

// ─── Selectors ────────────────────────────────────────────────────────────────

export const canUndo = (s: HistoryState & HistoryActions) => s.past.length > 0
export const canRedo = (s: HistoryState & HistoryActions) => s.future.length > 0
